import { useCallback, useEffect, useState } from 'react'

import { cn } from '#/shared/lib/utils'
import { ChevronDownIcon } from '#/shared/ui/icons'
import { Separator } from '#/shared/ui/separator'
import { Popover, PopoverTriggerButton } from '#/shared/ui/popover'

export interface SummaryItem {
  label: string
  value: string
}

export interface DataDropdownProps {
  from?: number
  to?: number
  total?: number
  summaryItems?: SummaryItem[]
  onPageChange?: (from: number, to: number) => void
  disabled?: boolean
}

function ChevronLeft(props: React.SVGProps<SVGSVGElement>) {
  return (
    <ChevronDownIcon
      {...props}
      className={`rotate-90 ${props.className ?? ''}`}
    />
  )
}

function ChevronRight(props: React.SVGProps<SVGSVGElement>) {
  return (
    <ChevronDownIcon
      {...props}
      className={`-rotate-90 ${props.className ?? ''}`}
    />
  )
}

function RangeInput({
  value,
  onChange,
  onEnter,
  invalid,
}: {
  value: string
  onChange: (v: string) => void
  onEnter: () => void
  invalid?: boolean
}) {
  return (
    <input
      type="number"
      min={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          onEnter()
        }
      }}
      aria-invalid={invalid ? true : undefined}
      className={cn(
        'h-8 w-24 appearance-none rounded-sm border bg-white px-1.5 text-right text-sm text-gray-900 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none focus:outline-none focus:ring-2',
        invalid
          ? 'border-destructive focus:ring-destructive'
          : 'border-gray-200 focus:ring-blue-500',
      )}
    />
  )
}

function NavButton({
  direction,
  disabled,
  onClick,
}: {
  direction: 'prev' | 'next'
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-8 w-6 items-center justify-center rounded-sm border border-transparent text-gray-900',
        disabled
          ? 'cursor-not-allowed opacity-30'
          : 'cursor-pointer hover:border-gray-200',
      )}
    >
      {direction === 'prev' ? (
        <ChevronLeft className="size-2.5" />
      ) : (
        <ChevronRight className="size-2.5" />
      )}
    </button>
  )
}

function parseRangeInput(raw: string): number | null {
  if (raw.trim() === '') return null
  const n = Number(raw)
  if (!Number.isFinite(n)) return null
  const floored = Math.floor(n)
  return floored >= 1 ? floored : null
}

interface RangeControls {
  fromInput: string
  toInput: string
  fromInvalid: boolean
  toInvalid: boolean
  prevDisabled: boolean
  nextDisabled: boolean
  setFrom: (v: string) => void
  setTo: (v: string) => void
  commit: () => void
  prev: () => void
  next: () => void
}

function useRangeControls(
  from: number,
  to: number,
  total: number,
  open: boolean,
  onPageChange?: (from: number, to: number) => void,
): RangeControls {
  // Inputs live as strings so the user can briefly clear the field while
  // retyping — otherwise `Number('')` would snap the value back to 0.
  const [fromInput, setFromInput] = useState(String(from))
  const [toInput, setToInput] = useState(String(to))

  // Resync to the committed window whenever it changes (a commit, the window
  // arrows, an external URL change) and every time the popover opens — so edits
  // typed but never confirmed with Enter are discarded on close.
  useEffect(() => {
    setFromInput(String(from))
    setToInput(String(to))
  }, [open, from, to])

  // Editing `from` auto-fills `to` to keep the current span as a suggestion
  // (typing `6000` on a 100-row window pre-fills `6099`); `to` stays editable.
  const windowSpan = Math.max(1, to - from + 1)
  const setFrom = useCallback(
    (v: string) => {
      setFromInput(v)
      const parsed = parseRangeInput(v)
      if (parsed !== null) setToInput(String(parsed + windowSpan - 1))
    },
    [windowSpan],
  )

  const parsedFrom = parseRangeInput(fromInput)
  const parsedTo = parseRangeInput(toInput)
  // `from` must be a real row within the dataset; `to` must sit at or above a
  // valid `from` (values past the total are accepted and clamped on commit).
  const fromInvalid = parsedFrom === null || parsedFrom > total
  const toInvalid =
    parsedTo === null || (parsedFrom !== null && parsedTo < parsedFrom)

  // Apply only on Enter (or the arrows). `to` is clamped to the total so the
  // window never claims rows past the end of the dataset.
  const commit = useCallback(() => {
    if (parsedFrom === null || parsedTo === null) return
    if (parsedFrom > total || parsedTo < parsedFrom) return
    onPageChange?.(parsedFrom, Math.min(parsedTo, total))
  }, [parsedFrom, parsedTo, total, onPageChange])

  const span = to - from + 1
  const prevDisabled = from <= 1
  const nextDisabled = from + span > total

  const prev = useCallback(() => {
    if (prevDisabled) return
    onPageChange?.(Math.max(1, from - span), from - 1)
  }, [from, span, onPageChange, prevDisabled])

  const next = useCallback(() => {
    if (nextDisabled) return
    const newFrom = from + span
    onPageChange?.(newFrom, Math.min(total, newFrom + span - 1))
  }, [from, span, total, onPageChange, nextDisabled])

  return {
    fromInput,
    toInput,
    fromInvalid,
    toInvalid,
    prevDisabled,
    nextDisabled,
    setFrom,
    setTo: setToInput,
    commit,
    prev,
    next,
  }
}

function DataDropdownPanel({
  from = 1,
  to = 100,
  total = 2500,
  summaryItems = [],
  onPageChange,
  open = true,
}: DataDropdownProps & { open?: boolean }) {
  const c = useRangeControls(from, to, total, open, onPageChange)

  return (
    <div className="flex min-w-68 flex-col gap-1 rounded-md bg-white p-1 shadow-popover">
      <div className="flex items-center gap-1 p-1.5">
        <div className="flex flex-1 items-center">
          <RangeInput
            value={c.fromInput}
            onChange={c.setFrom}
            onEnter={c.commit}
            invalid={c.fromInvalid}
          />
          <span className="px-1 text-sm text-gray-900">-</span>
          <RangeInput
            value={c.toInput}
            onChange={c.setTo}
            onEnter={c.commit}
            invalid={c.toInvalid}
          />
        </div>
        <div className="flex items-center gap-0.5">
          <NavButton
            direction="prev"
            disabled={c.prevDisabled}
            onClick={c.prev}
          />
          <NavButton
            direction="next"
            disabled={c.nextDisabled}
            onClick={c.next}
          />
        </div>
      </div>

      {summaryItems.length > 0 && (
        <>
          <Separator />
          {summaryItems.map((item) => (
            <div
              key={item.label}
              className="flex h-8 items-center justify-between px-2.5 text-sm text-gray-900"
            >
              <span>{item.label}</span>
              <span>{item.value}</span>
            </div>
          ))}
        </>
      )}
    </div>
  )
}

export function DataDropdown(props: DataDropdownProps) {
  const from = props.from ?? 1
  const to = props.to ?? 100
  const total = props.total ?? 2500
  const label = `${from}-${to} / ${total.toLocaleString()}`

  if (props.disabled) {
    // Render the trigger as a plain disabled button — bypassing Popover avoids
    // its click-to-open handler firing on a trigger the user shouldn't be
    // able to interact with.
    return (
      <PopoverTriggerButton
        open={false}
        className="pointer-events-none text-gray-900 opacity-50"
      >
        {label}
      </PopoverTriggerButton>
    )
  }

  return (
    <Popover
      // Auto edge detection: left-aligned under the trigger when there's room,
      // flipped to right-aligned when the trigger sits near the viewport's
      // right edge (the usual toolbar position) so the panel never spills
      // off-screen.
      align="auto"
      side="auto"
      trigger={(open) => (
        <PopoverTriggerButton open={open} className="text-gray-900">
          {label}
        </PopoverTriggerButton>
      )}
    >
      {(_close, open) => <DataDropdownPanel {...props} open={open} />}
    </Popover>
  )
}
